import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { getTheme } from './theme/modernTheme';
import { ThemeProvider as CustomThemeProvider, useTheme } from './contexts/ThemeContext';
import MainLayout from './layouts/MainLayout';
import { RouteTransition } from './components/PageTransition';
import DocumentWorkspace from './pages/DocumentWorkspace';
import InvestmentThesis from './pages/InvestmentThesis';
import FundThesisEditor from './pages/FundThesisEditor';
import Dashboard from './pages/Dashboard';
import AdminPanel from './pages/AdminPanel';
import DealSourcing from './pages/DealSourcing';
import DealSourcingDetail from './pages/DealSourcingDetail';
import FundOversight from './pages/FundOversight';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Chat from './pages/Chat';
import Integrations from './pages/Integrations';
import SelectOrganization from './pages/SelectOrganization';
import Login from './pages/Login';
import AcceptInvitation from './pages/AcceptInvitation';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import DDQ from './pages/DDQ';
import AnswerLibrary from './pages/AnswerLibrary';
import AIDrafts from './pages/AIDrafts';
import Export from './pages/Export';
import IngestDDQ from './pages/IngestDDQ';
import VersionHistory from './pages/VersionHistory';
// import SAMLCallback from './pages/SAMLCallback';
import ProtectedRoute from './components/ProtectedRoute';
import Unauthorized from './pages/Unauthorized';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/ErrorContext';
import { ErrorDialogProvider } from './contexts/ErrorDialogContext';
import AccessDeniedDialog from './components/AccessDeniedDialog';
 
const DialogController: React.FC = () => {
  const navigate = useNavigate();
  const [accessDeniedOpen, setAccessDeniedOpen] = useState(false);

  useEffect(() => {
    const handleAccessDenied = () => {
      setAccessDeniedOpen(true);
    };

    window.addEventListener('accessDenied', handleAccessDenied);

    return () => {
      window.removeEventListener('accessDenied', handleAccessDenied);
    };
  }, []);

  const handleSwitchOrg = () => {
    setAccessDeniedOpen(false);
    // Clear stale organization data from local storage
    localStorage.removeItem('organization');
    navigate('/select-organization');
  };

  return (
    <AccessDeniedDialog
      open={accessDeniedOpen}
      onClose={() => setAccessDeniedOpen(false)}
      onSwitchOrg={handleSwitchOrg}
    />
  );
};

 // Inner App component that uses the theme context
 const AppContent: React.FC = () => {
   const { isDark } = useTheme();
   const theme = getTheme(isDark);
 
   return (
     <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <AuthProvider>
          <Router>
            <ErrorDialogProvider>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/accept-invitation" element={<AcceptInvitation />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/select-organization" element={<SelectOrganization />} />
              
              {/* Protected routes */}
              <Route element={<ProtectedRoute />}>
                <Route element={<MainLayout />}>
                  <Route path="/" element={
                    <RouteTransition transitionType="fade" duration={300}>
                      <DocumentWorkspace />
                    </RouteTransition>
                  } />
                  <Route path="/projects" element={
                    <RouteTransition transitionType="fade" duration={300}>
                      <Projects />
                    </RouteTransition>
                  } />
                  <Route path="/projects/:projectId" element={
                    <RouteTransition transitionType="fade" duration={300}>
                      <ProjectDetail />
                    </RouteTransition>
                  } />
                  <Route element={<ProtectedRoute permissions={['cim']} />}>
                    <Route path="/investment-thesis" element={
                      <RouteTransition transitionType="fade" duration={300}>
                        <InvestmentThesis />
                      </RouteTransition>
                    } />
                    <Route path="/investment-thesis/:fundId" element={
                      <RouteTransition transitionType="fade" duration={300}>
                        <FundThesisEditor />
                      </RouteTransition>
                    } />
                    <Route path="/deal-sourcing" element={
                      <RouteTransition transitionType="fade" duration={300}>
                        <DealSourcing />
                      </RouteTransition>
                    } />
                    <Route path="/deals/:id" element={
                      <RouteTransition transitionType="fade" duration={300}>
                        <DealSourcingDetail />
                      </RouteTransition>
                    } />
                  </Route>
                  <Route path="/funds" element={
                    <RouteTransition transitionType="fade" duration={300}>
                      <FundOversight />
                    </RouteTransition>
                  } />
                  <Route path="/dashboard" element={
                    <RouteTransition transitionType="fade" duration={300}>
                      <Dashboard />
                    </RouteTransition>
                  } />
                  <Route path="/admin" element={
                    <RouteTransition transitionType="fade" duration={300}>
                      <AdminPanel />
                    </RouteTransition>
                  } />
                  <Route path="/integrations" element={
                    <RouteTransition transitionType="fade" duration={300}>
                      <Integrations />
                    </RouteTransition>
                  } />
                  <Route path="/chat" element={
                    <RouteTransition transitionType="fade" duration={300}>
                      <Chat />
                    </RouteTransition>
                  } />
                  <Route path="/profile" element={
                   <RouteTransition transitionType="fade" duration={300}>
                     <Profile />
                   </RouteTransition>
                 } />
                  <Route element={<ProtectedRoute permissions={['ddq']} />}>
                    <Route path="/ddq" element={
                      <RouteTransition transitionType="fade" duration={300}>
                        <DDQ />
                      </RouteTransition>
                    } />
                    <Route path="/library" element={
                      <RouteTransition transitionType="fade" duration={300}>
                        <AnswerLibrary />
                      </RouteTransition>
                    } />
                    <Route path="/ai-drafts/:questionId" element={
                      <RouteTransition transitionType="fade" duration={300}>
                        <AIDrafts />
                      </RouteTransition>
                    } />
                    <Route path="/export" element={
                      <RouteTransition transitionType="fade" duration={300}>
                        <Export />
                      </RouteTransition>
                    } />
                    <Route path="/ingest-ddq" element={
                      <RouteTransition transitionType="fade" duration={300}>
                        <IngestDDQ />
                      </RouteTransition>
                    } />
                    <Route path="/version-history/:answerId" element={
                      <RouteTransition transitionType="fade" duration={300}>
                        <VersionHistory />
                      </RouteTransition>
                    } />
                  </Route>
                </Route>
              </Route>
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <DialogController />
            </ErrorDialogProvider>
          </Router>
        </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <CustomThemeProvider>
        <AppContent />
    </CustomThemeProvider>
  );
}

export default App;
